import { makeSystemCableSpecGetter } from "./cable-spec";
import { getDefinition, type DefLookup } from "./catalog";
import { DEFAULT_AUTO_SIZE_VDROP_TARGET } from "./settings/auto-size-vdrop-target";
import { fuseExceedsTerminalLimit } from "./terminal-fuse-limit";
import type { Cable, System } from "./types";

/**
 * Pin each cable's current auto spec into its override fields so it stops
 * drifting. Only resolved calculations can be pinned; provisional current
 * estimates must not become declared material quantities. Fuses are refreshed
 * only where one is already set, leaving deliberately unfused legs alone.
 */
export type FreezeBuildPlan = {
  cables: Cable[];
  wiresFrozen: number;
  fusesRefreshed: number;
  unverifiedCableCount?: number;
};

export function planFreezeBuild(
  system: System,
  defLookup: DefLookup = getDefinition,
  autoSizeTargetPct: number = DEFAULT_AUTO_SIZE_VDROP_TARGET,
): FreezeBuildPlan {
  let wiresFrozen = 0;
  let fusesRefreshed = 0;
  let unverifiedCableCount = 0;

  // Strip the cable's own overrides first, or `computeCableSpec` echoes the
  // stored values back and freeze is a no-op on any already-pinned cable.
  const autoCables = system.cables.map((c) => ({
    ...c,
    wireSizeMm2Override: null,
    fuseAmpOverride: null,
  }));

  const specOf = makeSystemCableSpecGetter(
    { ...system, cables: autoCables }, defLookup, undefined, autoSizeTargetPct,
  );

  const cables = system.cables.map((cable, idx) => {
    const autoCable = autoCables[idx];
    const spec = specOf(autoCable);
    if (spec.currentBasis === "provisional-fallback" || spec.sizingVerificationRequired) {
      unverifiedCableCount++;
      return cable;
    }
    const next: Cable = { ...cable };
    if (!spec.manufacturerInstallationUnverified && cable.wireSizeMm2Override !== spec.wire.mm2) {
      next.wireSizeMm2Override = spec.wire.mm2;
      wiresFrozen++;
    }
    const fuse = spec.manufacturerFuseA ?? spec.fuse;
    if (!spec.fuseRecommendationUnavailableReason && !fuseExceedsTerminalLimit(spec, fuse) &&
        !fuseExceedsTerminalLimit(spec, cable.fuseAmpOverride) && cable.fuseAmpOverride != null && cable.fuseAmpOverride !== fuse) {
      next.fuseAmpOverride = fuse;
      fusesRefreshed++;
    }
    return next;
  });

  return { cables, wiresFrozen, fusesRefreshed, ...(unverifiedCableCount ? { unverifiedCableCount } : {}) };
}

/**
 * Clear every wire override so sizes track the live auto pick again. Fuses are
 * left alone: the engine doesn't recompute them live, so clearing one silently
 * unprotects a cable the user believes is fused.
 */
export type UnfreezeBuildPlan = {
  cables: Cable[];
  wiresCleared: number;
};

export function planUnfreezeBuild(system: System): UnfreezeBuildPlan {
  let wiresCleared = 0;
  const cables = system.cables.map((cable) => {
    if (cable.wireSizeMm2Override == null) return cable;
    wiresCleared++;
    return { ...cable, wireSizeMm2Override: null };
  });
  return { cables, wiresCleared };
}
